import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  LogOut,
  Search,
  Download,
  Mail,
  Inbox,
  CheckCircle2,
  Archive,
  Trash2,
  RefreshCw,
  X,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Logo from "../components/Logo";
import { adminApi, downloadCsv, logout, getUser, isAuthed } from "../lib/admin";

const PAGE_SIZE = 25;

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-700 border-blue-200",
  contacted: "bg-emerald-100 text-emerald-700 border-emerald-200",
  archived: "bg-gray-100 text-gray-600 border-gray-200",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("contacts");
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!isAuthed()) {
      navigate("/admin/login", { replace: true });
    }
  }, [navigate]);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/stats");
      setStats(data);
    } catch (e) {
      // silent
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const onLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-ink-900/5 bg-white">
        <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-5">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center" aria-label="SnapPay home">
              <Logo className="h-8" />
            </Link>
            <span className="hidden text-xs font-semibold uppercase tracking-widest text-ink-400 sm:inline">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-sm text-ink-500 sm:inline">
              Signed in as <strong className="text-ink-900">{getUser() || "admin"}</strong>
            </span>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 rounded-full border border-ink-900/10 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:border-brand-500 hover:text-brand-600"
            >
              <ExternalLink className="h-3.5 w-3.5" /> View site
            </Link>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 rounded-full bg-ink-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-ink-800"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-7xl px-5 py-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-ink-900">
              Form Submissions
            </h1>
            <p className="mt-1 text-sm text-ink-500">
              All contact requests and newsletter subscribers from your website.
            </p>
          </div>
        </div>

        <StatsRow stats={stats} />

        <div className="mt-8 inline-flex rounded-full border border-ink-900/10 bg-white p-1 shadow-sm">
          <TabButton active={tab === "contacts"} onClick={() => setTab("contacts")}>
            <Inbox className="h-4 w-4" /> Contact Form
            {stats?.contacts_total > 0 && (
              <span className="ml-1 rounded-full bg-ink-900/5 px-2 py-0.5 text-[11px] font-semibold text-ink-700">
                {stats.contacts_total}
              </span>
            )}
          </TabButton>
          <TabButton active={tab === "newsletter"} onClick={() => setTab("newsletter")}>
            <Mail className="h-4 w-4" /> Newsletter
            {stats?.newsletter_total > 0 && (
              <span className="ml-1 rounded-full bg-ink-900/5 px-2 py-0.5 text-[11px] font-semibold text-ink-700">
                {stats.newsletter_total}
              </span>
            )}
          </TabButton>
        </div>

        <div className="mt-5">
          {tab === "contacts" ? (
            <ContactsPanel onChange={loadStats} />
          ) : (
            <NewsletterPanel onChange={loadStats} />
          )}
        </div>
      </main>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
        active
          ? "bg-brand-700 text-white shadow-sm"
          : "text-ink-700 hover:text-brand-700"
      }`}
    >
      {children}
    </button>
  );
}

function StatsRow({ stats }) {
  const items = [
    { label: "Contact submissions", value: stats?.contacts_total ?? "—" },
    { label: "New / unhandled", value: stats?.contacts_new ?? "—" },
    { label: "Last 24h (contacts)", value: stats?.contacts_last_24h ?? "—" },
    { label: "Newsletter subscribers", value: stats?.newsletter_total ?? "—" },
  ];
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {items.map((it) => (
        <div
          key={it.label}
          className="rounded-2xl border border-ink-900/5 bg-white p-5 shadow-sm"
        >
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-400">
            {it.label}
          </div>
          <div className="mt-2 font-display text-3xl font-bold text-ink-900">
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contacts
// ---------------------------------------------------------------------------
function ContactsPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [skip, setSkip] = useState(0);
  const [selected, setSelected] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (q) params.q = q;
      if (statusFilter) params.status = statusFilter;
      const { data } = await adminApi.get("/contacts", { params });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, skip]);

  useEffect(() => {
    load();
  }, [load]);

  const onSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    load();
  };

  const updateStatus = async (id, contact_status) => {
    await adminApi.patch(`/contacts/${id}`, { contact_status });
    setItems((prev) =>
      prev.map((it) => (it.id === id ? { ...it, contact_status } : it))
    );
    if (selected?.id === id) setSelected({ ...selected, contact_status });
    onChange?.();
  };

  const doDelete = async (id) => {
    await adminApi.delete(`/contacts/${id}`);
    setItems((prev) => prev.filter((it) => it.id !== id));
    setTotal((t) => Math.max(0, t - 1));
    if (selected?.id === id) setSelected(null);
    setConfirmDelete(null);
    onChange?.();
  };

  const onExport = () =>
    downloadCsv("/contacts/export.csv", "contacts.csv").catch(() => {
      alert("Failed to export CSV");
    });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-t-2xl border border-b-0 border-ink-900/5 bg-white px-5 py-4">
        <form onSubmit={onSearch} className="flex flex-1 flex-wrap items-center gap-2">
          <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, business..."
              className="w-full rounded-full border border-ink-900/10 bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setSkip(0);
            }}
            className="rounded-full border border-ink-900/10 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none"
          >
            <option value="">All statuses</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="archived">Archived</option>
          </select>
          <button type="submit" className="btn-primary !py-2 !text-xs">
            Search
          </button>
        </form>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink-900/10 bg-white px-3 py-2 text-xs font-semibold text-ink-700 hover:border-brand-500 hover:text-brand-600"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            onClick={onExport}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink-900 px-3 py-2 text-xs font-semibold text-white hover:bg-ink-800"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-b-2xl border border-ink-900/5 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-widest text-ink-400">
              <tr>
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Email / Phone</th>
                <th className="px-5 py-3">Business</th>
                <th className="px-5 py-3">Volume</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-ink-400">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center text-ink-400">
                    No submissions yet.
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((it) => (
                  <tr
                    key={it.id}
                    onClick={() => setSelected(it)}
                    className="cursor-pointer border-t border-ink-900/5 hover:bg-brand-50/40"
                  >
                    <td className="whitespace-nowrap px-5 py-3 text-xs text-ink-500">
                      {formatDate(it.created_at)}
                    </td>
                    <td className="px-5 py-3 font-medium text-ink-900">
                      {fullName(it) || "—"}
                    </td>
                    <td className="px-5 py-3">
                      <div className="text-ink-900">{it.email}</div>
                      {it.phone && (
                        <div className="text-xs text-ink-500">{it.phone}</div>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div>{it.business_name || it.company || "—"}</div>
                      {(it.business_type || it.industry) && (
                        <div className="text-xs text-ink-500">
                          {it.business_type || it.industry}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-ink-700">{it.monthly_volume || "—"}</td>
                    <td className="px-5 py-3">
                      <StatusPill status={it.contact_status || "new"} />
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDelete(it);
                        }}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-400 hover:bg-red-50 hover:text-red-600"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        <Pager total={total} skip={skip} setSkip={setSkip} pageSize={PAGE_SIZE} />
      </div>

      {selected && (
        <ContactDrawer
          contact={selected}
          onClose={() => setSelected(null)}
          onUpdate={updateStatus}
          onDelete={(id) => setConfirmDelete(items.find((x) => x.id === id))}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="Delete submission?"
          body={`This will permanently delete the contact request from ${
            fullName(confirmDelete) || confirmDelete.email
          }.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => doDelete(confirmDelete.id)}
        />
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const cls = STATUS_COLORS[status] || STATUS_COLORS.new;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${cls}`}
    >
      {status}
    </span>
  );
}

function ContactDrawer({ contact, onClose, onUpdate, onDelete }) {
  const fields = useMemo(
    () => [
      ["First Name", contact.first_name],
      ["Last Name", contact.last_name],
      ["Legacy Name", contact.name],
      ["Email", contact.email],
      ["Phone", contact.phone],
      ["Business Name", contact.business_name || contact.company],
      ["Business Type", contact.business_type || contact.industry],
      ["Monthly Volume", contact.monthly_volume],
      ["Website", contact.website],
      ["Current Processor", contact.current_processor],
    ],
    [contact]
  );

  return (
    <div className="fixed inset-0 z-50 flex">
      <div
        className="flex-1 bg-ink-900/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <aside className="flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-ink-900/5 p-6">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-ink-400">
              {formatDate(contact.created_at)}
            </div>
            <h2 className="mt-1 font-display text-2xl font-bold text-ink-900">
              {fullName(contact) || contact.email}
            </h2>
            <div className="mt-2">
              <StatusPill status={contact.contact_status || "new"} />
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-2 text-ink-400 hover:bg-gray-100 hover:text-ink-900"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-ink-900/5 p-4">
          <button
            onClick={() => onUpdate(contact.id, "contacted")}
            disabled={contact.contact_status === "contacted"}
            className="inline-flex items-center gap-1.5 rounded-full bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Mark Contacted
          </button>
          <button
            onClick={() => onUpdate(contact.id, "new")}
            disabled={(contact.contact_status || "new") === "new"}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink-900/10 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:border-brand-500 disabled:opacity-50"
          >
            Mark New
          </button>
          <button
            onClick={() => onUpdate(contact.id, "archived")}
            disabled={contact.contact_status === "archived"}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink-900/10 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:border-brand-500 disabled:opacity-50"
          >
            <Archive className="h-3.5 w-3.5" /> Archive
          </button>
          <a
            href={`mailto:${contact.email}`}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink-900/10 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 hover:border-brand-500"
          >
            <Mail className="h-3.5 w-3.5" /> Reply
          </a>
          <button
            onClick={() => onDelete(contact.id)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </button>
        </div>

        <div className="flex-1 space-y-5 p-6">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-ink-400">
              Submission details
            </h3>
            <dl className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {fields.map(([label, value]) => (
                <div key={label} className="rounded-xl bg-gray-50 p-3">
                  <dt className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                    {label}
                  </dt>
                  <dd className="mt-1 break-words text-sm text-ink-900">
                    {value || <span className="text-ink-400">—</span>}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-ink-400">
              Message
            </h3>
            <p className="mt-2 whitespace-pre-wrap rounded-xl bg-gray-50 p-4 text-sm leading-relaxed text-ink-700">
              {contact.message || <span className="text-ink-400">—</span>}
            </p>
          </div>
        </div>
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Newsletter
// ---------------------------------------------------------------------------
function NewsletterPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [skip, setSkip] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip };
      if (q) params.q = q;
      const { data } = await adminApi.get("/newsletter", { params });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [q, skip]);

  useEffect(() => {
    load();
  }, [load]);

  const onSearch = (e) => {
    e.preventDefault();
    setSkip(0);
    load();
  };

  const doDelete = async (id) => {
    await adminApi.delete(`/newsletter/${id}`);
    setItems((prev) => prev.filter((it) => it.id !== id));
    setTotal((t) => Math.max(0, t - 1));
    setConfirmDelete(null);
    onChange?.();
  };

  const onExport = () =>
    downloadCsv("/newsletter/export.csv", "newsletter.csv").catch(() => {
      alert("Failed to export CSV");
    });

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-t-2xl border border-b-0 border-ink-900/5 bg-white px-5 py-4">
        <form onSubmit={onSearch} className="flex flex-1 items-center gap-2">
          <div className="relative min-w-[220px] flex-1 sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email..."
              className="w-full rounded-full border border-ink-900/10 bg-white py-2 pl-9 pr-3 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
          <button type="submit" className="btn-primary !py-2 !text-xs">
            Search
          </button>
        </form>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 rounded-full border border-ink-900/10 bg-white px-3 py-2 text-xs font-semibold text-ink-700 hover:border-brand-500 hover:text-brand-600"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
          <button
            onClick={onExport}
            className="inline-flex items-center gap-1.5 rounded-full bg-ink-900 px-3 py-2 text-xs font-semibold text-white hover:bg-ink-800"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-b-2xl border border-ink-900/5 bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-widest text-ink-400">
              <tr>
                <th className="px-5 py-3">Subscribed</th>
                <th className="px-5 py-3">Email</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={3} className="px-5 py-12 text-center text-ink-400">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-5 py-12 text-center text-ink-400">
                    No subscribers yet.
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((it) => (
                  <tr key={it.id} className="border-t border-ink-900/5">
                    <td className="whitespace-nowrap px-5 py-3 text-xs text-ink-500">
                      {formatDate(it.created_at)}
                    </td>
                    <td className="px-5 py-3 font-medium text-ink-900">{it.email}</td>
                    <td className="whitespace-nowrap px-5 py-3 text-right">
                      <a
                        href={`mailto:${it.email}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-400 hover:bg-brand-50 hover:text-brand-700"
                        aria-label="Email"
                      >
                        <Mail className="h-4 w-4" />
                      </a>
                      <button
                        onClick={() => setConfirmDelete(it)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-ink-400 hover:bg-red-50 hover:text-red-600"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <Pager total={total} skip={skip} setSkip={setSkip} pageSize={PAGE_SIZE} />
      </div>

      {confirmDelete && (
        <ConfirmDialog
          title="Remove subscriber?"
          body={`This will remove ${confirmDelete.email} from the newsletter list.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => doDelete(confirmDelete.id)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function Pager({ total, skip, setSkip, pageSize }) {
  if (total <= pageSize) return null;
  const start = total === 0 ? 0 : skip + 1;
  const end = Math.min(skip + pageSize, total);
  const canPrev = skip > 0;
  const canNext = end < total;
  return (
    <div className="flex items-center justify-between border-t border-ink-900/5 bg-gray-50 px-5 py-3 text-xs text-ink-500">
      <div>
        Showing <strong className="text-ink-900">{start}</strong>–
        <strong className="text-ink-900">{end}</strong> of{" "}
        <strong className="text-ink-900">{total}</strong>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setSkip(Math.max(0, skip - pageSize))}
          disabled={!canPrev}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-ink-900/10 bg-white disabled:opacity-40"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <button
          onClick={() => setSkip(skip + pageSize)}
          disabled={!canNext}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-ink-900/10 bg-white disabled:opacity-40"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ConfirmDialog({ title, body, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <h3 className="font-display text-lg font-bold text-ink-900">{title}</h3>
        <p className="mt-2 text-sm text-ink-500">{body}</p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-full border border-ink-900/10 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:border-brand-500"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function fullName(c) {
  if (c.first_name || c.last_name) {
    return [c.first_name, c.last_name].filter(Boolean).join(" ");
  }
  return c.name || "";
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}
